/** Lớp gọi REST: access token giữ trong bộ nhớ, refresh token ở cookie httpOnly; tự refresh 1 lần khi 401. */
export class ApiError extends Error { constructor(public status: number, message: string) { super(message); } }

let accessToken: string | null = null;
let characterId: string | null = localStorage.getItem('dn.character');
export interface SessionUser { id: string; email: string | null; emailVerified: boolean; isAdmin: boolean; isGuest: boolean }
export let currentUser: SessionUser | null = null;

export const getToken = () => accessToken;
/** Làm mới access token nếu sắp hết hạn (< 60 giây) – dùng trước khi bắt tay socket. */
export async function freshToken(): Promise<string | null> {
  try {
    const exp = accessToken ? JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp as number : 0;
    if (exp * 1000 - Date.now() < 60_000) await refresh();
  } catch { await refresh(); }
  return accessToken;
}
export const getCharacterId = () => characterId;
export function setCharacter(id: string | null) { characterId = id; if (id) localStorage.setItem('dn.character', id); else localStorage.removeItem('dn.character'); }

async function raw(path: string, init: RequestInit = {}, withChar = false): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  if (withChar && characterId) headers.set('X-Character-Id', characterId);
  return fetch(`/api/v1${path}`, { ...init, headers, credentials: 'same-origin' });
}

let refreshing: Promise<boolean> | null = null;
export function refresh(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const r = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'same-origin' });
      if (r.status === 204 || !r.ok) { accessToken = null; currentUser = null; return false; }
      const b = await r.json(); accessToken = b.accessToken; currentUser = b.user; return true;
    } catch { return false; } finally { setTimeout(() => (refreshing = null), 0); }
  })();
  return refreshing;
}

/** v0.11: tải tệp (multipart) – vd ảnh album. */
export async function apiUpload<T = unknown>(path: string, fd: FormData): Promise<T> {
  let r = await raw(path, { method: 'POST', body: fd }, true);
  if (r.status === 401) { if (await refresh()) r = await raw(path, { method: 'POST', body: fd }, true); }
  const b = await r.json().catch(() => ({})); if (!r.ok) throw new ApiError(r.status, (b as { message?: string }).message ?? 'Lỗi tải lên'); return b as T;
}
export async function api<T = unknown>(path: string, opts: { method?: string; body?: unknown; character?: boolean } = {}): Promise<T> {
  const init: RequestInit = { method: opts.method ?? (opts.body ? 'POST' : 'GET'), body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined };
  let r = await raw(path, init, opts.character);
  if (r.status === 401 && !path.startsWith('/auth/')) { if (await refresh()) r = await raw(path, init, opts.character); }
  if (r.status === 204) return undefined as T;
  const text = await r.text(); const data = text ? JSON.parse(text) : undefined;
  if (!r.ok) { const m = data?.message; throw new ApiError(r.status, Array.isArray(m) ? m.join('; ') : m ?? `Lỗi ${r.status}`); }
  return data as T;
}

/** Chơi ngay: tạo tài khoản khách, phiên giữ bằng cookie của trình duyệt này. */
export async function guestLogin() {
  const b = await api<{ accessToken: string; user: SessionUser }>('/auth/guest', { method: 'POST' });
  accessToken = b.accessToken; currentUser = b.user; return b.user;
}
/** Gắn email + mật khẩu cho tài khoản khách (giữ nguyên nhân vật, tiến độ). */
export async function claimAccount(email: string, password: string) {
  const b = await api<{ accessToken: string; user: SessionUser }>('/auth/claim', { body: { email, password } });
  accessToken = b.accessToken; currentUser = b.user; return b.user;
}

export async function login(email: string, password: string, register = false) {
  const b = await api<{ accessToken: string; user: SessionUser }>(register ? '/auth/register' : '/auth/login', { body: { email, password } });
  accessToken = b.accessToken; currentUser = b.user; return b.user;
}
export async function logout() { try { await api('/auth/logout', { method: 'POST' }); } finally { accessToken = null; currentUser = null; setCharacter(null); } }
